/**
 * TOSS Web Application - Client portal account block
 *
 * The portal's half of the shared account footer (`components/sidebar/SidebarAccount`): this file
 * decides what is in the menu, that one draws the row and owns the menu's opening. Written
 * separately from the internal half rather than reused, because that one
 *   - links to `ROUTES.profile.view`, an internal route `InternalOnlyRoute` refuses to a client
 *     session — every link to it would be a dead end;
 *   - logs out through `logoutWithPushCleanup()`, which is about FCM tokens the portal never
 *     registers (decision Р12: no push for this audience in Р1).
 *
 * ---------------------------------------------------------------------------
 * IT IS A MENU, NOT A LABEL WITH A BUTTON BESIDE IT (owner decision, 2026-08-24)
 * ---------------------------------------------------------------------------
 *
 * The sidebar's «Личное» block — «Уведомления» and «Профиль» — is gone; the bell in the header was
 * already printing the same unread count as the first row, and the second led to a screen this
 * block named the owner of without linking to. So the block became the control: the whole row
 * opens a menu with «Профиль» and «Выйти». On 2026-08-26 the internal rail followed it.
 *
 * The second line of the row is the COMPANY nowhere any more: `ClientHeader` names it on its own
 * line, and printing it here as well put one name twice on one screen. The collapsed tooltip still
 * carries it — there the header is the only other place it appears, and a 64 px rail shows neither.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Download, LogOut, Share, User } from 'lucide-react';
import { clientAuthApi } from '@/api/clientPortal/clientAuth.api';
import { ROUTES } from '@/constants';
import { useAuthStore, useCurrentUser } from '@/store/authStore';
import { SidebarAccount, sidebarAccountItemClass } from '@/components/sidebar';
import { useClientInstallOffer } from './useClientInstallOffer';

// ============================================================================
// Props
// ============================================================================

export interface ClientAccountBlockProps {
  /** Icon-only mode (tablet width, or a manually collapsed desktop sidebar). */
  collapsed: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ClientAccountBlock({ collapsed }: ClientAccountBlockProps): JSX.Element | null {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  /**
   * «Установить приложение» — the quiet half of the pair the floating prompt is the loud half of
   * (owner decision, 25.08.2026). The prompt says it once a week and gets out of the way; this row
   * is where the offer lives afterwards, for as long as the browser will still install anything.
   */
  const installOffer = useClientInstallOffer();
  const [showIOSHint, setShowIOSHint] = useState(false);

  // The iOS instruction belongs to one opening of the menu, not to the session. The menu itself
  // closes on navigation — that is `SidebarAccount`'s job.
  useEffect(() => {
    setShowIOSHint(false);
  }, [location.pathname]);

  if (!user) return null;

  const fullName = user.fullName || 'Пользователь';
  const companyName = user.customerName ?? '';
  const isProfileActive = location.pathname.startsWith(ROUTES.client.profile);

  /**
   * Sign out.
   *
   * `clientAuthApi.logout()` invalidates the refresh token server-side and clears the stored
   * tokens (it swallows its own network failure — a logout must never be blocked by one);
   * `authStore.logout()` then drops the in-memory session, which is what flips `isAuthenticated`
   * and lets the app purge the query and service-worker caches. The explicit navigate is belt and
   * braces: `ClientRoute` would bounce to the same place on the next render anyway.
   */
  const handleLogout = async (): Promise<void> => {
    setIsLoggingOut(true);
    try {
      await clientAuthApi.logout();
    } finally {
      useAuthStore.getState().logout();
      navigate(ROUTES.client.login, { replace: true });
    }
  };

  return (
    <SidebarAccount
      fullName={fullName}
      photoUrl={user.photoUrl}
      secondaryLabel={companyName || undefined}
      collapsed={collapsed}
      isProfileActive={isProfileActive}
    >
      {(close) => (
        <>
          {installOffer.isAvailable &&
            (showIOSHint ? (
              /* iOS has no install dialog to open, so the row turns into the two taps it would
                 have performed. */
              <p className="flex items-start gap-2.5 px-3 py-2 text-xs text-[var(--color-text-secondary)] border-b border-[var(--color-border-light)]">
                <Share className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                {installOffer.iosHint}
              </p>
            ) : (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  if (installOffer.isIOS) {
                    setShowIOSHint(true);
                    return;
                  }
                  void installOffer.install().then(close);
                }}
                className={`${sidebarAccountItemClass} border-b border-[var(--color-border-light)]`}
              >
                <Download
                  className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]"
                  aria-hidden="true"
                />
                Установить приложение
              </button>
            ))}

          <Link
            to={ROUTES.client.profile}
            role="menuitem"
            className={sidebarAccountItemClass}
            onClick={close}
          >
            <User
              className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]"
              aria-hidden="true"
            />
            Профиль
          </Link>

          <button
            type="button"
            role="menuitem"
            disabled={isLoggingOut}
            onClick={() => {
              void handleLogout();
            }}
            className={`${sidebarAccountItemClass} border-t border-[var(--color-border-light)]`}
          >
            <LogOut
              className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]"
              aria-hidden="true"
            />
            Выйти
          </button>
        </>
      )}
    </SidebarAccount>
  );
}

export default ClientAccountBlock;
